import { createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import api from 'api/axios/foka'
import moment from 'moment'
import { fmt } from 'components/calendar/calendarUtil'

const today = moment().format(fmt)

const initialState = {
  visible: false,
  isSubmitting: false,
  previewVisible: false,
  isLoading: false,
  loadingError: undefined,
  adGroups: [],
  form: {
    startDate: null,
    endDate: null,
    startDateDisabled: true,
    endDateDisabled: true,
  },
  preview: {
    startDate: null,
    endDate: null,
    startDateDisabled: true,
    endDateDisabled: true,
  },
}

export const showAdGroupDurationEdit = createAxiosThunk(
  'dashboard/showAdGroupDurationEdit',
  adGroupIds => api.ad.getAdGroups(adGroupIds)
)

export const updateAdGroupsDuration = createAxiosThunk(
  'dashboard/updateAdGroupsDuration',
  (noArgs, { getState }) => {
    const {
      adGroups,
      form: { startDate, endDate, startDateDisabled, endDateDisabled },
    } = getState().dashboard.operation.adGroupDurationEdit
    return api.ad.patchAdGroupsDuration({
      adGroupIds: adGroups.map(({ id }) => id),
      startDate: startDateDisabled ? null : startDate,
      endDate: endDateDisabled ? null : endDate,
    })
  }
)

const adGroupDurationEditSlice = createSlice({
  name: 'dashboard/adGroupDurationEdit',
  initialState,

  reducers: {
    hideAdGroupDurationEdit: () => initialState,
    setStartDate: (state, { payload }) => {
      state.form.startDate = payload
    },
    setEndDate: (state, { payload }) => {
      state.form.endDate = payload
    },
    setStartDateDisabled: (state, { payload: startDateDisabled }) => {
      if (state.form.startDateDisabled && !startDateDisabled) {
        state.form.startDate = today
      }
      state.form.startDateDisabled = startDateDisabled
      if (startDateDisabled) state.form.startDate = null
    },
    setEndDateDisabled: (state, { payload: endDateDisabled }) => {
      state.form.endDateDisabled = endDateDisabled
      if (endDateDisabled) state.form.endDate = null
    },
    showPreview: state => {
      state.preview.startDate = state.form.startDate
      state.preview.endDate = state.form.endDate
      state.preview.startDateDisabled = state.form.startDateDisabled
      state.preview.endDateDisabled = state.form.endDateDisabled
      state.previewVisible = true
    },
    hidePreview: state => {
      state.previewVisible = false
    },
  },

  extraReducers: builder => {
    builder.addCase(showAdGroupDurationEdit.pending, state => {
      state.isLoading = true
      state.visible = true
    })

    builder.addCase(showAdGroupDurationEdit.rejected, (state, { payload }) => {
      state.loadingError = payload
      state.isLoading = false
    })

    builder.addCase(
      showAdGroupDurationEdit.fulfilled,
      (state, { payload: { adGroupDtos } }) => {
        const adGroups = adGroupDtos.map(
          ({ id, name, impressionSchedule }) => ({
            id,
            name,
            impressionSchedule,
          })
        )
        state.adGroups = adGroups
        const hasSameValue =
          adGroups.length > 1 &&
          adGroups.every((value, index, array) => {
            const duration = value.impressionSchedule
            const other = array[0].impressionSchedule
            return (
              duration.startDate === other.startDate &&
              duration.endDate === other.endDate
            )
          })
        if (adGroups.length === 1 || hasSameValue) {
          const [first] = adGroups
          const {
            impressionSchedule: { startDate, endDate },
          } = first
          state.form.startDate = startDate
          state.form.startDateDisabled = false
          state.form.endDate = endDate
          state.form.endDateDisabled = endDate === null
        }
        state.isLoading = false
      }
    )

    builder.addCase(updateAdGroupsDuration.pending, state => {
      state.isSubmitting = true
    })

    builder.addCase(updateAdGroupsDuration.fulfilled, state => {
      state.isSubmitting = false
    })

    builder.addCase(updateAdGroupsDuration.rejected, state => {
      state.isSubmitting = false
    })
  },
})
export const {
  hideAdGroupDurationEdit,
  showPreview,
  hidePreview,
  setStartDate,
  setEndDate,
  setEndDateDisabled,
  setStartDateDisabled,
} = adGroupDurationEditSlice.actions
export default adGroupDurationEditSlice.reducer
